import axios, { type AxiosResponse } from 'axios';
import { logger } from '@trading-utils/logger';

import { processBackendError } from '@/utils/error.utils';

import i18n from '@/i18n/config';

import { apiRoutes } from '@/config/apiRoutes';

export type PricingIndex = {
  id: number;
  name: string;
  timeLimitLower: string;
  timeLimitUpper: string;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreatePricingIndexPayload = {
  name: string;
  timeLimitLower: string;
  timeLimitUpper: string;
};

export const getPricingIndices = async (): Promise<PricingIndex[]> => {
  const url = `${apiRoutes.base}${apiRoutes.pricingIndices.base}`;

  try {
    const response: AxiosResponse<PricingIndex[]> = await axios.get(url);

    logger.info('Fetched pricing indices');

    return response.data;
  } catch (error) {
    logger.error('Error fetching pricing indices', { url, error });
    throw processBackendError(error, i18n.t('errors.pricingIndices.errorGetPricingIndices'));
  }
};

export const createPricingIndex = async (payload: CreatePricingIndexPayload): Promise<PricingIndex> => {
  const url = `${apiRoutes.base}${apiRoutes.pricingIndices.base}`;

  try {
    const response: AxiosResponse<PricingIndex> = await axios.post(url, payload);

    logger.info('Pricing index created successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error creating pricing index', { url, error });
    throw processBackendError(error, i18n.t('errors.pricingIndices.errorCreatePricingIndex'));
  }
};

export const updatePricingIndex = async (
  id: number,
  payload: Partial<CreatePricingIndexPayload>
): Promise<PricingIndex> => {
  const url = `${apiRoutes.base}${apiRoutes.pricingIndices.base}/${id}`;

  try {
    const response: AxiosResponse<PricingIndex> = await axios.patch(url, payload);

    logger.info('Pricing index updated successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error updating pricing index', { url, error });
    throw processBackendError(error, i18n.t('errors.pricingIndices.errorUpdatePricingIndex'));
  }
};

export const deletePricingIndex = async (id: number): Promise<PricingIndex> => {
  const url = `${apiRoutes.base}${apiRoutes.pricingIndices.base}/${id}`;

  try {
    const response: AxiosResponse<PricingIndex> = await axios.delete(url);

    logger.info('Pricing index deleted successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error deleting pricing index', { url, error });
    throw processBackendError(error, i18n.t('errors.pricingIndices.errorDeletePricingIndex'));
  }
};
